// src/redux/slices/blogsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { blogsService } from '../../services/blogsService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchBlogs = createAsyncThunk(
    'blogs/fetchBlogs',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await blogsService.getBlogs(params)
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const fetchBlogBySlug = createAsyncThunk(
    'blogs/fetchBlogBySlug',
    async (slug, { rejectWithValue }) => {
        try {
            const response = await blogsService.getBlogBySlug(slug)

            return response?.data?.blog
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const likeBlog = createAsyncThunk(
    'blogs/likeBlog',
    async (slug, { rejectWithValue }) => {
        try {
            const response = await blogsService.likeBlog(slug)
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const blogsSlice = createSlice({
    name: 'blogs',
    initialState: {
        blogs: [],
        currentBlog: null,
        categories: [],
        loading: false,
        hasMore: true,
        error: null
    },
    reducers: {
        clearCurrentBlog: (state) => {
            state.currentBlog = null
        },
        clearError: (state) => {
            state.error = null
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Blogs
            .addCase(fetchBlogs.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchBlogs.fulfilled, (state, action) => {
                state.loading = false
                const newBlogs = action.payload.data || action.payload
                if (action.payload.page > 1) {
                    state.blogs = [...state.blogs, ...newBlogs]
                } else {
                    state.blogs = newBlogs
                }
                state.hasMore = newBlogs.length === (action.payload.pagination?.limit || 10)
                const categories = new Set();
                action.payload.data.blogs.forEach(blog => {
                    if (blog.hero?.type) {
                        categories.add(blog.hero.type);
                    }
                });

                state.categories = Array.from(categories);
                state.error = null
            })
            .addCase(fetchBlogs.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Fetch Blog by Slug
            .addCase(fetchBlogBySlug.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchBlogBySlug.fulfilled, (state, action) => {
                state.loading = false
                state.currentBlog = action.payload
                state.error = null
            })
            .addCase(fetchBlogBySlug.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Like Blog
            .addCase(likeBlog.fulfilled, (state, action) => {
                if (state.currentBlog && state.currentBlog.slug === action.payload.slug) {
                    state.currentBlog.likes = action.payload.likes
                    state.currentBlog.userLiked = action.payload.userLiked
                }
                const blogIndex = state.blogs.findIndex(blog => blog.slug === action.payload.slug)
                if (blogIndex !== -1) {
                    state.blogs[blogIndex].likes = action.payload.likes
                    state.blogs[blogIndex].userLiked = action.payload.userLiked
                }
            })
    }
})

export const { clearCurrentBlog, clearError } = blogsSlice.actions
export default blogsSlice.reducer